import { useOrderDashboardReport } from '@/api/hooks/useOrderDashboard'
import type { EnvMode } from '@/utils/envMode'
import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Alert } from 'antd'
import type { DashboardWindow } from '../../dateRange'
import { EventMetricsCard } from './EventMetricsCard'
import { OverviewCard } from './OverviewCard'
import { PartnerMetricsCard } from './PartnerMetricsCard'
import { ReferenceDataCard } from './ReferenceDataCard'
import { IssuesSection } from './issues/IssuesSection'
import type { OrderDashboardFilters } from './useOrderDashboardFilters'

interface OrdersDashboardProps {
  reportWindow: DashboardWindow
  /** Test/Prod, or undefined for every instance. Owned by the page header
   * alongside the window, so both panes report on the same set. */
  mode: EnvMode | undefined
  /** Owned by the page header, which clears all of it -- see the hook. */
  filters: OrderDashboardFilters
}

export function OrdersDashboard({
  reportWindow,
  mode,
  filters,
}: OrdersDashboardProps) {
  const { data, error, isLoading } = useOrderDashboardReport(reportWindow, mode)
  const { metrics: metricFilters, setMetrics: setMetricFilters } = filters

  return (
    <Horizontal
      gap={8}
      style={{ flex: 1, minHeight: 0, alignItems: 'stretch' }}
    >
      <Vertical
        gap={8}
        scroll
        style={{ flex: '0 0 40%', minWidth: 0, paddingRight: 4 }}
      >
        {error && (
          <Alert
            type='error'
            showIcon
            message='Could not load order metrics'
            description={String(error)}
          />
        )}
        <OverviewCard report={data} loading={isLoading} />
        <PartnerMetricsCard
          report={data}
          loading={isLoading}
          selectedTenantId={metricFilters.partner?.tenantId ?? null}
          onSelectPartner={(partner) =>
            setMetricFilters((current) => ({
              ...current,
              partner: partner
                ? {
                    tenantId: partner.tenant_id,
                    tenantName: partner.tenant_name,
                  }
                : null,
            }))
          }
        />
        <EventMetricsCard
          report={data}
          loading={isLoading}
          selectedEvent={metricFilters.event}
          selectedPartnerName={metricFilters.partner?.tenantName ?? null}
          onSelectEvent={(event) =>
            setMetricFilters((current) => ({ ...current, event }))
          }
        />
        <ReferenceDataCard />
      </Vertical>
      <Vertical scroll style={{ flex: '0 0 60%', minWidth: 0 }}>
        <IssuesSection
          reportWindow={reportWindow}
          mode={mode}
          metricFilters={metricFilters}
          onChangeMetricFilters={setMetricFilters}
          selection={filters.issues}
          onChangeSelection={filters.setIssues}
        />
      </Vertical>
    </Horizontal>
  )
}
